import React, { useState } from "react";
import { todayLocal } from "../utils/dateLocal";
import axios from "axios";
import { FilePlus2, Save, LogOut, Check } from "lucide-react";
import "./RechercheModal.css";
import "./FermetureLotModal.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyInfo = {
  nom: "", prenom: "", date_naissance: "", avocat: "", avocat_confie: "", demandes: [],
};

export default function FermetureLotModal({ onClose, noReg, noBur }) {
  const [noRef, setNoRef] = useState("");
  const [reg, setReg] = useState(noReg || "");
  const [bur, setBur] = useState(noBur || "");
  const [dateFerm, setDateFerm] = useState(todayLocal());
  const [code, setCode] = useState("");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultats, setResultats] = useState(null);
  const [accum, setAccum] = useState([]);          // grille A : demandes à fermer (multi-attestations)
  const [confirmOpen, setConfirmOpen] = useState(false); // popup de confirmation Oui/Non
  const [popup, setPopup] = useState("");          // popup d'erreur de validation du no de référence

  const nouveau = () => {
    setNoRef(""); setReg(noReg || ""); setBur(noBur || "");
    setDateFerm(todayLocal()); setCode(""); setInfo(null);
    setError(""); setResultats(null); setAccum([]); setConfirmOpen(false); setPopup("");
  };

  // Masque du no de référence : 8 chiffres + "-" + 2 chiffres (########-##).
  const handleRefChange = (e) => {
    let v = e.target.value.replace(/\D/g, "").slice(0, 10);
    if (v.length > 8) v = v.slice(0, 8) + "-" + v.slice(8);
    setNoRef(v);
  };

  // Bouton "Valider" : vérifie le no de référence. Si la demande existe ET est
  // admise (adda654=1) ou refusée (adda670=1) → affiche les détails dessous.
  // Sinon → popup (n'existe pas / statut non fermable).
  const valider = async () => {
    setError(""); setResultats(null); setInfo(null); setPopup("");
    const refVal = noRef.trim();
    if (refVal.length !== 11) {
      setPopup("Le numéro de référence doit contenir 11 caractères (format 00000000-00).");
      return;
    }
    setLoading(true);
    try {
      const regVal = reg.trim(), burVal = bur.trim();
      const { data } = await axios.get(`${API}/fermeture-lot/demandes`, {
        params: { no_ref: refVal, no_reg: regVal, no_bur: burVal },
      });
      setInfo({ ...data, no_ref: refVal, no_reg: regVal, no_bur: burVal });
    } catch (e) {
      setPopup(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Bouton vert (dans les détails) : ajoute les demandes validées à la grille A
  // avec la date + le code choisis, puis nettoie pour saisir un nouveau no réf.
  const ajouterAGrille = () => {
    setError("");
    if (!info) return;
    if (!dateFerm) { setError("Indiquez la date de fermeture."); return; }
    if (!code) { setError("Choisissez un code de fermeture."); return; }
    // La date de fermeture ne peut être antérieure à la date d'émission de l'attestation.
    if (info.date_emission && dateFerm < info.date_emission) {
      setPopup(`La date de fermeture (${dateFerm}) ne peut pas être antérieure à la date d'émission de l'attestation (${info.date_emission}).`);
      return;
    }
    const refVal = info.no_ref, regVal = info.no_reg, burVal = info.no_bur;
    const codeVal = parseInt(code, 10);
    const abrege = ((info.codes || []).find(c => String(c.code) === String(code)) || {}).abrege || "";
    const dateVal = dateFerm;
    const found = info.demandes || [];
    setAccum(prev => {
      const map = {};
      prev.forEach(r => { map[r.noseqdaj] = r; });
      found.forEach(d => {
        map[d.noseqdaj] = {
          noseqdaj: d.noseqdaj, nodossier: d.nodossier, code_nature: d.code_nature,
          no_ref: refVal, code: codeVal, code_abrege: abrege,
          date_fermeture: dateVal, reg: regVal, bur: burVal,
        };
      });
      return Object.values(map);
    });
    // Nettoyage pour saisir un nouveau No de référence.
    setNoRef(""); setInfo(null); setDateFerm(todayLocal()); setCode("");
  };

  // Enregistrer : ferme TOUTES les demandes de la grille A, regroupées par
  // (code + date + région + bureau) car elles peuvent venir de No réf différents.
  const enregistrer = () => {
    setError("");
    if (!accum.length) { setError("Aucune demande à fermer."); return; }
    setConfirmOpen(true);
  };

  const confirmerFermeture = async () => {
    setConfirmOpen(false);
    setError(""); setLoading(true);
    try {
      const groups = {};
      accum.forEach(r => {
        const key = `${r.code}|${r.date_fermeture}|${r.reg}|${r.bur}`;
        if (!groups[key]) groups[key] = { code: r.code, date: r.date_fermeture, reg: r.reg, bur: r.bur, seqs: [] };
        groups[key].seqs.push(r.noseqdaj);
      });
      let nbFermees = 0, nbErreurs = 0, allRes = [];
      const dejaAll = [];
      for (const g of Object.values(groups)) {
        const { data } = await axios.post(`${API}/fermeture-lot/fermer`, {
          noseqdajs: g.seqs, code: g.code, date_fermeture: g.date,
          no_reg: g.reg, no_bur: g.bur, confirmer_deja_ferme: false,
        });
        data.resultats.forEach(r => {
          if (r.statut === "deja_fermee") dejaAll.push({ ...r, _g: g });
          else allRes.push(r);
        });
        nbFermees += data.nb_fermees; nbErreurs += data.nb_erreurs;
      }
      if (dejaAll.length) {
        const liste = dejaAll.map(r => `${r.noseqdaj} (${r.message})`).join("\n");
        if (window.confirm(`Ces demandes sont déjà fermées :\n${liste}\n\nDésirez-vous modifier la date ou le code de fermeture ?`)) {
          const byG = {};
          dejaAll.forEach(r => {
            const k = `${r._g.code}|${r._g.date}|${r._g.reg}|${r._g.bur}`;
            if (!byG[k]) byG[k] = { g: r._g, seqs: [] };
            byG[k].seqs.push(r.noseqdaj);
          });
          for (const { g, seqs } of Object.values(byG)) {
            const rerun = await axios.post(`${API}/fermeture-lot/fermer`, {
              noseqdajs: seqs, code: g.code, date_fermeture: g.date,
              no_reg: g.reg, no_bur: g.bur, confirmer_deja_ferme: true,
            });
            allRes = allRes.concat(rerun.data.resultats);
            nbFermees += rerun.data.nb_fermees; nbErreurs += rerun.data.nb_erreurs;
          }
        } else {
          allRes = allRes.concat(dejaAll.map(({ _g, ...r }) => r));
        }
      }
      setResultats({ nb_fermees: nbFermees, nb_erreurs: nbErreurs, resultats: allRes });
      // Retire de la grille les demandes effectivement fermées.
      const closed = new Set(allRes.filter(r => r.statut === "fermee").map(r => r.noseqdaj));
      setAccum(prev => prev.filter(r => !closed.has(r.noseqdaj)));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const Label = ({ children }) => <div className="fl-label">{children}</div>;

  return (
    <div className="rech-overlay">
      <div className="fl-modal" data-testid="fermlot-modal">
        {/* Barre de titre */}
        <div className="fl-titlebar">
          <span>Fermeture des demandes reliées à une attestation</span>
          <button className="rech-close" onClick={onClose} data-testid="fermlot-close" title="Fermer">×</button>
        </div>

        {/* Barre d'outils : Nouveau / Enregistrer / Retour */}
        <div className="fl-toolbar">
          <button className="fl-tool" onClick={nouveau} disabled={loading} data-testid="fermlot-btn-nouveau">
            <FilePlus2 size={20} /><span>Nouveau</span>
          </button>
          <button className="fl-tool" onClick={enregistrer}
                  disabled={loading || !accum.length} data-testid="fermlot-btn-enregistrer">
            <Save size={20} /><span>Enregistrer</span>
          </button>
          <button className="fl-tool" onClick={onClose} data-testid="fermlot-btn-retour">
            <LogOut size={20} /><span>Retour</span>
          </button>
        </div>

        <div className="fl-body">
          {/* En-tête : Région + Bureau (non modifiables) + No de Référence + Valider */}
          <div className="fl-header">
            <div className="fl-form">
              <div className="fl-row">
                <Label>No de Référence</Label>
                <input className="fl-input fl-input-sm fl-ro" value={reg} readOnly
                       title="Région" data-testid="fermlot-reg" />
                <input className="fl-input fl-input-sm fl-ro" value={bur} readOnly
                       title="Bureau" data-testid="fermlot-bur" />
                <input className="fl-input" value={noRef} maxLength={11}
                       onChange={handleRefChange}
                       onKeyDown={e => e.key === "Enter" && valider()}
                       placeholder="00000000-00" autoFocus data-testid="fermlot-noref" />
              </div>
            </div>
            <button className="fl-green" onClick={valider} disabled={loading}
                    title="Valider le no de référence" data-testid="fermlot-btn-valider">
              <Check size={40} strokeWidth={4} />
            </button>
          </div>

          {error && <div className="rech-error" data-testid="fermlot-error">{error}</div>}

          {/* Bloc information (lecture seule) + résultat de recherche — visible
              seulement après une recherche d'attestation. */}
          {info && (
            <>
              <div className="fl-info">
                <div className="fl-row">
                  <Label>No de Référence</Label>
                  <input className="fl-input fl-input-sm fl-ro" value={info?.no_reg || ""} readOnly
                         title="Région" data-testid="fermlot-info-reg" />
                  <input className="fl-input fl-input-sm fl-ro" value={info?.no_bur || ""} readOnly
                         title="Bureau" data-testid="fermlot-info-bur" />
                  <input className="fl-input fl-ro" value={info?.no_ref || ""} readOnly data-testid="fermlot-info-noref" />
                </div>
                <div className="fl-row">
                  <Label>Nom</Label>
                  <input className="fl-input fl-ro" value={info?.nom || ""} readOnly data-testid="fermlot-nom" />
                </div>
                <div className="fl-row">
                  <Label>Prénom</Label>
                  <input className="fl-input fl-ro" value={info?.prenom || ""} readOnly data-testid="fermlot-prenom" />
                </div>
                <div className="fl-row">
                  <Label>Date de naissance</Label>
                  <input className="fl-input fl-ro" value={info?.date_naissance || ""} readOnly data-testid="fermlot-ddn" />
                </div>
                <div className="fl-row">
                  <Label>Statut</Label>
                  <input className="fl-input fl-ro" value={info?.statut || ""} readOnly data-testid="fermlot-statut" />
                  <input className="fl-input fl-ro" style={{ flex: "0 0 120px", marginLeft: 4 }}
                         value={info?.date_emission || ""} readOnly
                         title="Date d'émission de l'attestation" data-testid="fermlot-date-emission" />
                </div>
                <div className="fl-row">
                  <Label>Avocat signataire</Label>
                  <input className="fl-input fl-ro" value={info?.avocat || ""} readOnly data-testid="fermlot-avosign" />
                </div>
                <div className="fl-row">
                  <Label wide>Avocat confié à</Label>
                  <input className="fl-input fl-ro" value={info?.avocat_confie || ""} readOnly data-testid="fermlot-avoconfie" />
                </div>
                <div className="fl-row">
                  <Label>Date de fermeture</Label>
                  <input className="fl-input" type="date" value={dateFerm} max={todayLocal()}
                         onChange={e => setDateFerm(e.target.value)} data-testid="fermlot-date" />
                </div>
                <div className="fl-row">
                  <Label>Code de fermeture</Label>
                  <select className="fl-input" value={code} onChange={e => setCode(e.target.value)}
                          data-testid="fermlot-code">
                    <option value="">  </option>
                    {(info?.codes || []).map(c => (
                      <option key={c.code} value={c.code}>{c.abrege} — {c.description}</option>
                    ))}
                  </select>
                  <button className="fl-green fl-green-sm" onClick={ajouterAGrille} disabled={loading}
                          title="Ajouter à la liste des demandes à fermer" data-testid="fermlot-btn-ajouter">
                    <Check size={26} strokeWidth={4} />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Grille A : demandes qui seront fermées (toutes, sans case à cocher) */}
          <div className="fl-listbox" data-testid="fermlot-accum-grid">
            {accum.length ? (
              <table className="fl-table">
                <thead>
                  <tr>
                    <th className="fl-nowrap">No référence</th><th className="fl-nowrap">Séquence</th><th>No dossier</th>
                    <th>Code nature</th><th>Code fermeture</th><th>Date fermeture</th>
                    <th style={{ width: 34 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {accum.map((r, i) => (
                    <tr key={r.noseqdaj} data-testid={`fermlot-accum-row-${i}`}>
                      <td className="fl-nowrap" style={{ fontWeight: 600 }}>{r.no_ref}</td>
                      <td className="fl-nowrap">{r.noseqdaj}</td><td>{r.nodossier}</td><td>{r.code_nature}</td>
                      <td>{r.code_abrege || r.code}</td><td>{r.date_fermeture}</td>
                      <td style={{ textAlign: "center" }}>
                        <button className="fl-row-remove"
                                onClick={() => setAccum(prev => prev.filter(x => x.noseqdaj !== r.noseqdaj))}
                                title="Retirer cette demande de la liste"
                                data-testid={`fermlot-accum-remove-${i}`}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="fl-empty" data-testid="fermlot-accum-empty">Aucune demande à fermer.</div>
            )}
          </div>

          {resultats && (
            <div className="fl-resultats" data-testid="fermlot-resultats">
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                Résultat : {resultats.nb_fermees} fermée(s), {resultats.nb_erreurs} erreur(s)
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                {resultats.resultats.map(r => (
                  <li key={r.noseqdaj} style={{ color: r.statut === "fermee" ? "#1a7a1a" : "#a00" }}>
                    {r.noseqdaj} — {r.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {confirmOpen && (
          <div className="fl-confirm-overlay" data-testid="fermlot-confirm">
            <div className="fl-confirm-box">
              <div className="fl-confirm-msg" data-testid="fermlot-confirm-msg">
                Voulez-vous fermer <strong>{accum.length}</strong> demande(s)&nbsp;?
              </div>
              <div className="fl-confirm-btns">
                <button className="fl-btn fl-btn-primary" onClick={confirmerFermeture}
                        data-testid="fermlot-confirm-oui">Oui</button>
                <button className="fl-btn" onClick={() => setConfirmOpen(false)}
                        data-testid="fermlot-confirm-non">Non</button>
              </div>
            </div>
          </div>
        )}

        {popup && (
          <div className="fl-confirm-overlay" data-testid="fermlot-popup">
            <div className="fl-confirm-box">
              <div className="fl-confirm-msg" data-testid="fermlot-popup-msg">{popup}</div>
              <div className="fl-confirm-btns">
                <button className="fl-btn fl-btn-primary" onClick={() => setPopup("")}
                        data-testid="fermlot-popup-ok">OK</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
